import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, mergeProps as _mergeProps, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "breadcrumb text-racq-dark-grey pb-8"
}
const _hoisted_2 = ["href"]

import { usePageStore, useAccountStore, useRoadsideAssistance } from '@/store';
import { useRoute } from 'vue-router';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { RoadsideProductType } from '@/utils/roadside-helpers';
import { useI18n } from 'vue-i18n';
import { getInsuranceTypeDisplayName } from '@/interfaces';
import { stringify } from 'querystring';
import { computedAsync } from '@vueuse/core';
import { computed } from 'vue';
import { showCombined } from '@/utils/common-helper';

enum Bookmarks {
  tierDescription = 'tierDescription',
  policyId = 'policyId',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SelfServiceBreadcrumbs',
  setup(__props) {

const pageStore = usePageStore();
const accountStore = useAccountStore();
const rsaStore = useRoadsideAssistance();
const route = useRoute();

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;
const linkProps = computedAsync(() => updateBreadCrumbs(), [{ title: 'Loading...' }], { lazy: true });

// Wait for identity to avoid policy not found
const identityLoaded = computed(() => accountStore.identityDetails?.portfolio);

/**
 * Replace breadcrumb title and url bookmarks
 * @ref Bookmarks
 */
const updateBreadCrumbs = async () => {
  const breadcrumbs: Record<string, any>[] = [];
  for (const index in pageStore.breadcrumbs) {
    const item = pageStore.breadcrumbs[index];
    // Replace bookmark in href e.g {{policyId}}
    if (item.value?.includes(Bookmarks.policyId))
      item.value = replaceBookmark(Bookmarks.policyId, item.value, route.query?.policyId as string);

    // Replace bookmark in label {{tierDescription}} => RACQ Everyday
    const title = await getBreadCrumbTitle(item.name);

    // Special case for roadside details / insurance details sub pages
    if (item.name != title)
      item.value += `${item.value?.includes('?') ? '&' : '?'}${stringify((route.query as Record<string, string>) ?? {})}`;

    const bindings: Record<string, any> = {
      href: item.value,
      title,
    };
    if (Number(index) == pageStore.breadcrumbs.length - 1) bindings.disabled = true;
    breadcrumbs.push(bindings);
  }
  return breadcrumbs;
};

/**
 * Convert breadcrumb title to policy name
 * @param breadcrumbTitle 00312321
 * @return RACQ Everyday
 */
const getBreadCrumbTitle = async (breadcrumbTitle: string) => {
  if (!breadcrumbTitle?.includes(Bookmarks.tierDescription)) return breadcrumbTitle;

  try {
    if (route.query.policyId || route.query.policyid) {
      const usePolicyId = route.query.policyId ?? route.query.policyid;
      const status = route.query.status;
      const rsaPolicy = accountStore.identityDetails?.portfolio?.assistanceProducts?.find(
        (p) =>
          p.policyId === usePolicyId && (!status ? true : p.status?.toLowerCase() === status?.toString()?.toLowerCase())
      );
      const roadsidePolicyType = rsaPolicy?.roadsidePolicyType;
      const tierDescription =
        roadsidePolicyType !== RoadsideProductType.Cmo
          ? rsaPolicy?.tierDescription
          : (await rsaStore.getRsaProductContent(rsaPolicy?.productCode))?.tierDescription;
      return tierDescription
        ? replaceBookmark(Bookmarks.tierDescription, breadcrumbTitle, tierDescription)
        : breadcrumbTitle;
    } else if (route.query.policyNumber || route.query.policynumber) {
      const usePolicyNumber = route.query.policyNumber ?? route.query.policynumber;
      const currentPolicy = accountStore.identityDetails?.portfolio?.insurancePolicies?.find(
        (p) => p.policyNumber === usePolicyNumber
      );

      let insuranceDisplayName;
      if (showCombined(currentPolicy?.coverages ?? [])) {
        insuranceDisplayName = dictionary.IT_Combined;
      } else {
        insuranceDisplayName = getInsuranceTypeDisplayName(
          dictionary,
          currentPolicy?.coverages[0]?.coverageCode ?? '',
          false
        );
      }

      return insuranceDisplayName
        ? replaceBookmark(Bookmarks.tierDescription, breadcrumbTitle, insuranceDisplayName)
        : breadcrumbTitle;
    }
  } catch {
    // do nothing
  }
  return breadcrumbTitle;
};

return (_ctx: any,_cache: any) => {
  return (identityLoaded.value)
    ? (_openBlock(), _createElementBlock("ul", _hoisted_1, [
        _createElementVNode("li", null, [
          _createElementVNode("a", {
            href: _unref(pageStore).homePage.value,
            class: "no-underline hover:!underline hover:!decoration-[10%]"
          }, _toDisplayString(_unref(pageStore).homePage.name), 9, _hoisted_2)
        ]),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(linkProps), (breadcrumb, index) => {
          return (_openBlock(), _createElementBlock("li", { key: index }, [
            _createElementVNode("a", _mergeProps({ ref_for: true }, breadcrumb, { class: "no-underline hover:!underline hover:!decoration-[10%]" }), _toDisplayString(breadcrumb.title), 17)
          ]))
        }), 128))
      ]))
    : _createCommentVNode("", true)
}
}

})